export interface OptionItem {
  id: string;
  label: string;
  labelAr: string;
  icon?: string;
  /** Amenity grouping bucket (see AMENITY_GROUPS). Only used by AMENITIES_LIST. */
  group?: string;
}

export type PropertyCondition = 'new' | 'excellent' | 'good' | 'fair' | 'needs_work' | '';

// Property types matching mobile app
export const RESIDENTIAL_TYPES: OptionItem[] = [
  { id: 'apartment', label: 'Apartment', labelAr: 'شقة', icon: 'ri-building-4-line' },
  { id: 'villa', label: 'Villa / House', labelAr: 'فيلا', icon: 'ri-home-5-line' },
  { id: 'floor', label: 'Floor', labelAr: 'دور', icon: 'ri-stack-line' },
  { id: 'building', label: 'Building', labelAr: 'عمارة', icon: 'ri-building-line' },
  { id: 'land', label: 'Land', labelAr: 'أرض', icon: 'ri-landscape-line' },
  { id: 'rest_house', label: 'Rest House', labelAr: 'استراحة', icon: 'ri-home-heart-line' },
  { id: 'chalet', label: 'Chalet', labelAr: 'شاليه', icon: 'ri-home-wifi-line' },
  { id: 'room', label: 'Room', labelAr: 'غرفة', icon: 'ri-door-open-line' },
  { id: 'townhouse', label: 'Townhouse', labelAr: 'تاون هاوس', icon: 'ri-home-4-line' },
  { id: 'studio', label: 'Studio', labelAr: 'استوديو', icon: 'ri-home-3-line' },
  { id: 'penthouse', label: 'Penthouse', labelAr: 'بنتهاوس', icon: 'ri-building-2-line' },
  { id: 'duplex', label: 'Duplex', labelAr: 'دوبلكس', icon: 'ri-home-6-line' },
];

export const COMMERCIAL_TYPES: OptionItem[] = [
  { id: 'office', label: 'Office', labelAr: 'مكتب', icon: 'ri-building-3-line' },
  { id: 'commercial_building', label: 'Commercial Building', labelAr: 'عمارة تجارية', icon: 'ri-building-line' },
  { id: 'warehouse', label: 'Warehouse', labelAr: 'مستودع', icon: 'ri-store-3-line' },
  { id: 'commercial_land', label: 'Commercial Land', labelAr: 'أرض تجارية', icon: 'ri-landscape-line' },
  { id: 'industrial_land', label: 'Industrial Land', labelAr: 'أرض صناعية', icon: 'ri-landscape-line' },
  { id: 'shop', label: 'Shop', labelAr: 'محل', icon: 'ri-store-2-line' },
  { id: 'showroom', label: 'Showroom', labelAr: 'معرض', icon: 'ri-store-line' },
  { id: 'farm', label: 'Farm', labelAr: 'مزرعة', icon: 'ri-plant-line' },
  { id: 'agricultural_land', label: 'Agricultural Land', labelAr: 'أرض زراعية', icon: 'ri-seedling-line' },
  { id: 'mall', label: 'Mall', labelAr: 'مول', icon: 'ri-store-3-line' },
  { id: 'hotel', label: 'Hotel', labelAr: 'فندق', icon: 'ri-hotel-line' },
  { id: 'workshop', label: 'Workshop', labelAr: 'ورشة', icon: 'ri-tools-line' },
  { id: 'factory', label: 'Factory', labelAr: 'مصنع', icon: 'ri-building-2-line' },
  { id: 'school', label: 'School', labelAr: 'مدرسة', icon: 'ri-school-line' },
  { id: 'health_center', label: 'Health Center', labelAr: 'مركز صحي', icon: 'ri-hospital-line' },
  { id: 'station', label: 'Station', labelAr: 'محطة', icon: 'ri-gas-station-line' },
];

// House attachment style (Rightmove-style) — only for residential house types.
export type HouseStyle = 'detached' | 'semi_detached' | 'terraced' | '';
export const HOUSE_STYLES: OptionItem[] = [
  { id: 'detached', label: 'Detached', labelAr: 'منفصل', icon: 'ri-home-5-line' },
  { id: 'semi_detached', label: 'Semi-detached', labelAr: 'شبه منفصل', icon: 'ri-home-6-line' },
  { id: 'terraced', label: 'Terraced', labelAr: 'متصل', icon: 'ri-community-line' },
];

// Amenity groups (Bayut-style) — render the grid under labeled sub-headings for
// scannability instead of one flat wall of 19 chips.
export const AMENITY_GROUPS: { id: string; label: string; labelAr: string }[] = [
  { id: 'interior', label: 'Interior & Comfort', labelAr: 'الداخل والراحة' },
  { id: 'building', label: 'Building & Access', labelAr: 'المبنى والخدمات' },
  { id: 'outdoor', label: 'Outdoor & Leisure', labelAr: 'الخارج والترفيه' },
  { id: 'pets', label: 'Pets', labelAr: 'الحيوانات الأليفة' },
];

// Amenities. Icons come from the unified domain registry keyed by these ids
// (components/base/icons/domain/registry.tsx, rendered via <DomainIcon id=…>);
// the detail page resolves free-text DB names through utils/amenityIcons.ts.
// NOTE: there is intentionally NO "parking" amenity — parking is captured once,
// by the dedicated "Parking Spaces" number field (the payload derives hasParking
// from it). A second toggle here would be a redundant, conflicting entry.
export const AMENITIES_LIST: OptionItem[] = [
  // Interior & comfort
  { id: 'central_ac', label: 'Central AC', labelAr: 'تكييف مركزي', group: 'interior' },
  { id: 'smart_home', label: 'Smart Home', labelAr: 'منزل ذكي', group: 'interior' },
  { id: 'maid_room', label: 'Maid Room', labelAr: 'غرفة خادمة', group: 'interior' },
  { id: 'driver_room', label: 'Driver Room', labelAr: 'غرفة سائق', group: 'interior' },
  { id: 'guest_house', label: 'Guest House', labelAr: 'بيت ضيافة', group: 'interior' },
  { id: 'storage', label: 'Storage', labelAr: 'تخزين', group: 'interior' },
  { id: 'basement', label: 'Basement', labelAr: 'قبو', group: 'interior' },
  // Building & access
  { id: 'elevator', label: 'Elevator', labelAr: 'مصعد', group: 'building' },
  { id: 'intercom', label: 'Intercom', labelAr: 'اتصال داخلي', group: 'building' },
  { id: 'concierge', label: 'Concierge', labelAr: 'خدمة استقبال', group: 'building' },
  { id: 'security', label: 'Security', labelAr: 'حراسة', group: 'building' },
  // Outdoor & leisure
  { id: 'balcony', label: 'Balcony', labelAr: 'شرفة', group: 'outdoor' },
  { id: 'roof', label: 'Private Roof', labelAr: 'سطح خاص', group: 'outdoor' },
  { id: 'garden', label: 'Garden', labelAr: 'حديقة', group: 'outdoor' },
  { id: 'courtyard', label: 'Courtyard', labelAr: 'فناء', group: 'outdoor' },
  { id: 'pool', label: 'Swimming Pool', labelAr: 'مسبح', group: 'outdoor' },
  { id: 'gym', label: 'Gym', labelAr: 'صالة رياضية', group: 'outdoor' },
  { id: 'playground', label: 'Playground', labelAr: 'ملعب أطفال', group: 'outdoor' },
  // Pets (rental-only — gated out for sale listings)
  { id: 'pet_friendly', label: 'Pet Friendly', labelAr: 'مسموح بالحيوانات', group: 'pets' },
];

// Utilities list (matching mobile). Expanded with the Saudi REGA service set
// (sewage / phone / fibre / flood drainage / gas) shown on the details page.
// `internet` and `fiber` are the SAME connection at different tech levels, so
// they're mutually exclusive (see UTILITY_MUTEX_GROUPS) — a property has either
// a standard line or fibre, never both. Labels make the distinction explicit.
export const UTILITIES_LIST: OptionItem[] = [
  { id: 'water', label: 'Water', labelAr: 'ماء', icon: 'ri-drop-line' },
  { id: 'electricity', label: 'Electricity', labelAr: 'كهرباء', icon: 'ri-flashlight-line' },
  { id: 'sewage', label: 'Sewage', labelAr: 'صرف صحي', icon: 'ri-drop-fill' },
  { id: 'gas', label: 'Gas', labelAr: 'غاز', icon: 'ri-fire-line' },
  { id: 'phone', label: 'Phone Line', labelAr: 'هاتف', icon: 'ri-phone-line' },
  { id: 'internet', label: 'Standard Internet', labelAr: 'إنترنت عادي', icon: 'ri-wifi-line' },
  { id: 'fiber', label: 'Fiber Internet', labelAr: 'إنترنت ألياف', icon: 'ri-router-line' },
  { id: 'flood_drainage', label: 'Flood Drainage', labelAr: 'تصريف الفيضانات', icon: 'ri-water-flash-line' },
];

// Mutually-exclusive utility groups: selecting any id in a group clears the
// other ids in the same group. Standard vs. fibre internet is one connection.
export const UTILITY_MUTEX_GROUPS: string[][] = [['internet', 'fiber']];

// ── REGA / details-page option sets ─────────────────────────────────────────

// Residential occupancy type (نوع السكن) — family-only vs. singles/bachelor.
export const RESIDENTIAL_TYPE_OPTIONS: OptionItem[] = [
  { id: 'families', label: 'Families', labelAr: 'عوائل', icon: 'ri-group-line' },
  { id: 'singles', label: 'Singles', labelAr: 'أفراد', icon: 'ri-user-line' },
];

// Property facade direction (واجهة العقار) — matches the backend Orientation enum.
export const ORIENTATION_OPTIONS: OptionItem[] = [
  { id: 'NORTH', label: 'North', labelAr: 'شمالية', icon: 'ri-compass-3-line' },
  { id: 'SOUTH', label: 'South', labelAr: 'جنوبية', icon: 'ri-compass-3-line' },
  { id: 'EAST', label: 'East', labelAr: 'شرقية', icon: 'ri-compass-3-line' },
  { id: 'WEST', label: 'West', labelAr: 'غربية', icon: 'ri-compass-3-line' },
  { id: 'CORNER', label: 'Corner', labelAr: 'زاوية', icon: 'ri-shape-2-line' },
];

// Title-deed status (حالة الصك) — matches the backend DeedStatus enum.
export const DEED_STATUS_OPTIONS: OptionItem[] = [
  { id: 'ELECTRONIC', label: 'Electronic', labelAr: 'إلكتروني', icon: 'ri-file-shield-2-line' },
  { id: 'PAPER', label: 'Paper', labelAr: 'ورقي', icon: 'ri-file-paper-2-line' },
  { id: 'NONE', label: 'None', labelAr: 'لا يوجد', icon: 'ri-file-close-line' },
];

// Construction status (حالة البناء) — matches the backend ConstructionStatus enum.
export const CONSTRUCTION_STATUS_OPTIONS: OptionItem[] = [
  { id: 'READY', label: 'Ready', labelAr: 'جاهز', icon: 'ri-checkbox-circle-line' },
  { id: 'UNDER_CONSTRUCTION', label: 'Under Construction', labelAr: 'تحت الإنشاء', icon: 'ri-hammer-line' },
  { id: 'OFF_PLAN', label: 'Off-plan', labelAr: 'على الخارطة', icon: 'ri-draft-line' },
];

// Owner type (نوع المالك) — matches the backend OwnerType enum.
export const OWNER_TYPE_OPTIONS: OptionItem[] = [
  { id: 'INDIVIDUAL', label: 'Individual', labelAr: 'فرد', icon: 'ri-user-line' },
  { id: 'COMPANY', label: 'Company', labelAr: 'شركة', icon: 'ri-building-line' },
  { id: 'MULTIPLE', label: 'Multiple', labelAr: 'متعدد', icon: 'ri-group-line' },
];

// Listing status (owner-editable). 'pending' is surfaced as "Under Negotiation".
// `rented` only applies to rentals and `sold` only to sales — getListingStatusOptions
// filters accordingly so an owner can't mark a sale "Rented" or vice-versa.
export const LISTING_STATUS_OPTIONS: (OptionItem & { listing?: 'sale' | 'rent' })[] = [
  { id: 'available', label: 'Available', labelAr: 'متاح', icon: 'ri-checkbox-circle-line' },
  { id: 'pending', label: 'Under Negotiation', labelAr: 'تحت التفاوض', icon: 'ri-loop-right-line' },
  { id: 'rented', label: 'Rented', labelAr: 'مؤجر', icon: 'ri-key-line', listing: 'rent' },
  { id: 'sold', label: 'Sold', labelAr: 'مباع', icon: 'ri-price-tag-3-line', listing: 'sale' },
];

export function getListingStatusOptions(listingType: 'sale' | 'rent') {
  return LISTING_STATUS_OPTIONS.filter((o) => !o.listing || o.listing === listingType);
}

// Construction stage (حالة الإنشاء) — proper terms for an under-construction
// property; "Final Touches" is the correct label for "final adjustments".
export const CONSTRUCTION_STAGE_OPTIONS: OptionItem[] = [
  { id: 'FOUNDATION', label: 'Foundation', labelAr: 'الأساسات', icon: 'ri-hammer-line' },
  { id: 'STRUCTURE', label: 'Structure', labelAr: 'الهيكل الإنشائي', icon: 'ri-building-4-line' },
  { id: 'FINISHING', label: 'Finishing', labelAr: 'التشطيبات', icon: 'ri-paint-brush-line' },
  { id: 'FINAL_TOUCHES', label: 'Final Touches', labelAr: 'اللمسات الأخيرة', icon: 'ri-sparkling-2-line' },
];

// Advertising channels the listing is published through (قنوات الإعلان).
export const AD_CHANNEL_OPTIONS: OptionItem[] = [
  { id: 'licensed_platform', label: 'Licensed Platform', labelAr: 'منصة مرخصة', icon: 'ri-global-line' },
  { id: 'billboard', label: 'Billboard', labelAr: 'لوحة إعلانية', icon: 'ri-advertisement-line' },
  { id: 'social_media', label: 'Social Media', labelAr: 'وسائل التواصل', icon: 'ri-share-line' },
  { id: 'newspaper', label: 'Newspaper', labelAr: 'صحيفة', icon: 'ri-newspaper-line' },
];

// Property condition options (matching mobile)
export const PROPERTY_CONDITIONS: OptionItem[] = [
  { id: 'new', label: 'New', labelAr: 'جديد' },
  { id: 'excellent', label: 'Excellent', labelAr: 'ممتاز' },
  { id: 'good', label: 'Good', labelAr: 'جيد' },
  { id: 'fair', label: 'Fair', labelAr: 'مقبول' },
  { id: 'needs_work', label: 'Needs Work', labelAr: 'يحتاج صيانة' },
];
